<template>
    <!-- the filters a list uses less often, behind one pill: a right side panel with a section per
         filter that edits a draft, only Apply hands it to the list (tasks#211) -->
    <div class="dd-more-filters">
        <v-btn
            :class="[
                'dd-filter-pill',
                { 'dd-filter-pill--active': activeCount > 0 },
            ]"
            @click="open = true"
        >
            {{ buttonText }}
        </v-btn>
        <v-dialog
            v-model="open"
            eager
            content-class="dd-side-panel dd-more-filters-dialog"
            transition="slide-x-reverse-transition"
        >
            <v-card class="dd-more-filters-panel">
                <div class="dd-more-filters-panel__head">
                    <h2>{{ title }}</h2>
                    <v-btn
                        icon
                        :aria-label="$t('actions.close')"
                        @click="open = false"
                    >
                        <v-icon>close</v-icon>
                    </v-btn>
                </div>
                <v-card-text @keydown.enter="applyFromInput">
                    <section
                        v-for="(field, index) in fields"
                        :key="field.key + field.type + index"
                        class="dd-more-filters-panel__section"
                    >
                        <chip-filter
                            v-if="field.inputType === 'checkbox'"
                            :value="draft"
                            :field="field"
                            as-switch
                            @input="setDraft"
                        ></chip-filter>
                        <template v-else>
                            <button
                                type="button"
                                class="dd-more-filters-panel__toggle"
                                :aria-expanded="String(isExpanded(index))"
                                @click="toggle(index)"
                            >
                                <span class="dd-more-filters-panel__label">
                                    {{ label(field) }}
                                </span>
                                <span class="dd-more-filters-panel__summary">
                                    {{ summary(field) }}
                                </span>
                                <v-icon>{{
                                    isExpanded(index)
                                        ? 'expand_less'
                                        : 'expand_more'
                                }}</v-icon>
                            </button>
                            <div
                                v-show="isExpanded(index)"
                                class="dd-more-filters-panel__body"
                            >
                                <filter-input
                                    :value="draft"
                                    :field="field"
                                    single-month
                                    @input="setDraft"
                                ></filter-input>
                            </div>
                        </template>
                    </section>
                </v-card-text>
                <v-card-actions>
                    <v-btn
                        text
                        class="dd-more-filters-panel__reset"
                        @click="reset"
                    >
                        {{ tx('labels.reset', 'Reset') }}
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" @click="apply">
                        {{ tx('actions.apply', 'Apply') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import ChipFilter from './ChipFilter'
import FilterInput from './FilterInput'
import {
    isFilterActive,
    readFilter,
    readRangeEnd,
    withoutFilters,
} from './filterState'

export default {
    components: { ChipFilter, FilterInput },
    props: {
        value: {
            type: Object,
            required: true,
        },
        fields: {
            type: Array,
            required: true,
        },
        // a filter whose value an active quick-filter chip shows (Today) is not counted here
        ownedByChip: {
            type: Function,
            default: () => false,
        },
    },
    data() {
        return {
            open: false,
            draft: cloneDeep(this.value),
            expanded: [],
        }
    },
    computed: {
        activeCount() {
            return this.fields.filter(
                (field) =>
                    !this.ownedByChip(field) &&
                    isFilterActive(this.value, field)
            ).length
        },
        title() {
            return this.tx('labels.more_filters', 'More filters')
        },
        buttonText() {
            return this.activeCount
                ? `${this.title} (${this.activeCount})`
                : this.title
        },
    },
    watch: {
        // every section that holds a value opens with the panel
        open(open) {
            if (!open) return
            this.draft = cloneDeep(this.value)
            this.expanded = this.fields
                .map((field, index) =>
                    field.inputType !== 'checkbox' &&
                    !this.ownedByChip(field) &&
                    isFilterActive(this.value, field)
                        ? index
                        : null
                )
                .filter((index) => index !== null)
        },
        value: {
            deep: true,
            handler(value) {
                if (!this.open) this.draft = cloneDeep(value)
            },
        },
    },
    methods: {
        tx(key, fallback) {
            return this.$te(key) ? this.$t(key) : fallback
        },
        label(field) {
            return field.translatedLabel || this.$t(field.label)
        },
        summary(field) {
            if (this.ownedByChip(field) || !isFilterActive(this.draft, field))
                return this.tx('labels.any', 'Any')
            const value = readFilter(this.draft, field)
            if (field.inputType === 'daterange') {
                const format = (date) => this.$date(date).format('D MMM')
                const end = readRangeEnd(this.draft, field)
                if (!value) return `until ${format(end)}`
                if (!end) return `from ${format(value)}`
                return format(value) === format(end)
                    ? format(value)
                    : `${format(value)} – ${format(end)}`
            }
            if (Array.isArray(value)) return `${value.length} selected`
            if (field.inputType === 'search') return value
            return '1 selected'
        },
        isExpanded(index) {
            return this.expanded.includes(index)
        },
        toggle(index) {
            this.expanded = this.isExpanded(index)
                ? this.expanded.filter((i) => i !== index)
                : [...this.expanded, index]
        },
        setDraft(value) {
            this.draft = value
        },
        reset() {
            this.draft = withoutFilters(this.draft, this.fields)
        },
        apply() {
            this.$emit('input', cloneDeep(this.draft))
            this.open = false
        },
        // Enter in a text field (coupon code) applies; the field writes the draft one tick later
        applyFromInput(event) {
            if (event.target.tagName === 'INPUT') this.$nextTick(this.apply)
        },
    },
}
</script>

<style lang="scss">
.dd-side-panel.dd-more-filters-dialog > .v-card.dd-more-filters-panel {
    // the side panels' 16px sides (_theme.scss) for the title, the sections and the buttons alike
    // (the title sat on 24px, the rows on 16px; sweep 2026-11-04)
    > .v-card__text {
        padding: 0 16px 24px;
        overflow-x: hidden;
    }

    > .v-card__actions {
        padding: 16px;
        border-top: 1px solid #e7e7e7;
    }
}

.dd-more-filters-panel {
    &__head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        padding: 24px 16px 8px;

        h2 {
            margin: 0;
            color: #191919;
            font-size: 22px;
            font-weight: 700;
            line-height: 28px;
        }
    }

    &__section {
        border-bottom: 1px solid #e7e7e7;

        // a checkbox filter is a setting row: label left, switch right
        .v-input--switch.dd-filter-switch {
            margin: 0;
            padding: 16px 0;

            .v-input__slot {
                flex-direction: row-reverse;
                justify-content: space-between;
            }

            .v-label {
                color: #191919;
                font-size: 14px;
                font-weight: 600;
                line-height: 20px;
            }
        }
    }

    &__toggle {
        display: flex;
        align-items: center;
        gap: 12px;
        width: 100%;
        min-height: 56px;
        color: #191919;
        text-align: left;

        &:focus-visible {
            outline: 2px solid #191919;
            outline-offset: 2px;
        }

        .v-icon {
            flex: 0 0 auto;
            color: #191919 !important;
        }
    }

    &__label {
        flex: 1 1 auto;
        min-width: 0;
        font-size: 14px;
        font-weight: 600;
        line-height: 20px;
    }

    &__summary {
        flex: 0 1 auto;
        min-width: 0;
        overflow: hidden;
        color: #606060;
        font-size: 14px;
        line-height: 20px;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__body {
        padding-bottom: 16px;

        .search-field {
            width: 100%;
        }

        // the option list sits on the panel's own 16px sides
        .dd-option {
            padding-right: 0;
            padding-left: 0;
        }

        .dd-option-list__search {
            margin: 0 0 8px;
        }
    }

    .v-btn.dd-more-filters-panel__reset {
        padding: 0 8px;
        color: #191919;
        text-decoration: underline;
    }
}
</style>
